import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Award, Coins, Check, Clock, AlertTriangle } from 'lucide-react';
import { LOYALTY_TIERS, getTierProgress } from '@/lib/loyaltyTiers';
import CompensationActionsPanel from './CompensationActionsPanel';

const POINTS_PER_DISCOUNT_POUND = 100;
const INACTIVITY_EXPIRY_MONTHS = 12;

// Admin-facing loyalty view for a single customer. Shows the current points
// balance, lifetime earned (which drives tiers), the current tier + progress to
// the next, and the full tier ladder with the benefit unlocked at each level.
export default function CustomerLoyaltyPanel({ email }) {
  const { data: ledger = [], isLoading } = useQuery({
    queryKey: ['crm-loyalty', email],
    queryFn: () => base44.entities.LoyaltyLedger.filter({ customer_email: email }, '-created_date', 200),
    enabled: !!email,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['crm-consumer-account', email],
    queryFn: () => base44.entities.ConsumerAccount.filter({ email }),
    enabled: !!email,
  });
  const account = accounts && accounts[0];
  const lastActivity = account?.last_activity_at;
  const tallyResetAt = account?.tally_reset_at;
  const expiryDate = lastActivity
    ? new Date(new Date(lastActivity).setMonth(new Date(lastActivity).getMonth() + INACTIVITY_EXPIRY_MONTHS))
    : null;
  const now = new Date();
  const daysUntilExpiry = expiryDate ? Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;

  if (isLoading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const balance = ledger.reduce((s, r) => s + Number(r.points || 0), 0);
  const lifetimeEarned = ledger.filter((r) => Number(r.points) > 0).reduce((s, r) => s + Number(r.points), 0);
  const lifetimeRedeemed = ledger.filter((r) => Number(r.points) < 0).reduce((s, r) => s + Math.abs(Number(r.points)), 0);
  const { current, next, progress, pointsToNext } = getTierProgress(lifetimeEarned);

  return (
    <div className="space-y-6">
      {/* Balance + current tier */}
      <div className="bg-gradient-to-br from-primary/15 via-[#0d0d0f] to-[#0d0d0f] border border-primary/30 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Award className="w-4 h-4 text-primary" />
          <p className="text-primary text-[10px] font-bold tracking-widest">LOYALTY</p>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">CURRENT BALANCE</p>
            <p className="text-white font-black text-3xl leading-none flex items-center gap-2">
              <Coins className="w-5 h-5 text-primary" />
              {balance.toLocaleString()}
            </p>
            <p className="text-white/30 text-[10px] mt-1.5">£{(balance / POINTS_PER_DISCOUNT_POUND).toFixed(2)} redeemable value</p>
          </div>
          <div className="text-right">
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">TIER</p>
            <p className="font-black text-2xl leading-none" style={{ color: current.color }}>{current.name}</p>
          </div>
        </div>
      </div>

      {/* Activity & expiry tracker */}
      {lastActivity ? (
        <div className={`border p-4 flex items-center gap-3 ${daysUntilExpiry != null && daysUntilExpiry <= 60 ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/5 bg-[#0d0d0f]'}`}>
          <Clock className={`w-4 h-4 shrink-0 ${daysUntilExpiry != null && daysUntilExpiry <= 60 ? 'text-amber-400' : 'text-white/40'}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-white/40 text-[10px] font-bold tracking-widest">LAST ACTIVITY</p>
              {daysUntilExpiry != null && daysUntilExpiry <= 60 && (
                <span className="inline-flex items-center gap-1 text-amber-400 text-[10px] font-bold tracking-widest uppercase">
                  <AlertTriangle className="w-3 h-3" /> {daysUntilExpiry <= 0 ? 'EXPIRED' : `${daysUntilExpiry} DAYS TO EXPIRY`}
                </span>
              )}
            </div>
            <p className="text-white/70 text-sm mt-1">
              {new Date(lastActivity).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              {expiryDate && <> · <span className={daysUntilExpiry != null && daysUntilExpiry <= 60 ? 'text-amber-400 font-bold' : 'text-white/40'}>expires {expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span></>}
            </p>
            {tallyResetAt && (
              <p className="text-white/30 text-[10px] mt-1">Tally last reset {new Date(tallyResetAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-white/5 bg-[#0d0d0f] p-4 flex items-center gap-3">
          <Clock className="w-4 h-4 text-white/30 shrink-0" />
          <p className="text-white/40 text-xs">No activity tracked yet — run the loyalty activity backfill in Maintenance.</p>
        </div>
      )}

      {/* Tier progress to next */}
      {next ? (
        <div className="bg-[#0d0d0f] border border-white/5 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white/40 text-[10px] font-bold tracking-widest">PROGRESS TO {next.name.toUpperCase()}</p>
            <p className="text-white text-xs font-bold">{lifetimeEarned.toLocaleString()} / {next.threshold.toLocaleString()}</p>
          </div>
          <div className="w-full h-2 bg-white/5 overflow-hidden">
            <div className="h-full" style={{ width: `${progress}%`, backgroundColor: next.color }} />
          </div>
          <p className="text-white/30 text-xs mt-3">
            {pointsToNext > 0
              ? `${pointsToNext.toLocaleString()} more lifetime points to reach ${next.name} and unlock:`
              : `Reached ${next.name} — benefit unlocked:`}
          </p>
          <p className="text-white/70 text-xs mt-1.5 leading-relaxed">{next.benefit}</p>
        </div>
      ) : (
        <div className="bg-[#0d0d0f] border border-primary/20 p-5 flex items-center gap-2">
          <Check className="w-4 h-4 text-primary shrink-0" />
          <p className="text-white text-xs">Highest tier reached — all benefits unlocked.</p>
        </div>
      )}

      {/* Lifetime stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0d0d0f] border border-white/5 p-4">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">LIFETIME EARNED</p>
          <p className="text-white font-black text-lg mt-1">{lifetimeEarned.toLocaleString()}</p>
        </div>
        <div className="bg-[#0d0d0f] border border-white/5 p-4">
          <p className="text-white/30 text-[9px] font-bold tracking-widest">REDEEMED</p>
          <p className="text-white font-black text-lg mt-1">{lifetimeRedeemed.toLocaleString()}</p>
        </div>
        <div className="bg-primary/5 border border-primary/20 p-4">
          <p className="text-primary text-[9px] font-bold tracking-widest">BALANCE</p>
          <p className="text-primary font-black text-lg mt-1">{balance.toLocaleString()}</p>
        </div>
      </div>

      {/* Compensation actions */}
      <CompensationActionsPanel customerEmail={email} customerName={account?.customer_name} />

      {/* Tier ladder — milestones with benefits */}
      <div>
        <p className="text-primary text-[10px] font-bold tracking-widest mb-4 pb-3 border-b border-white/5">TIER MILESTONES &amp; BENEFITS</p>
        <div className="space-y-2">
          {LOYALTY_TIERS.map((t) => {
            const achieved = lifetimeEarned >= t.threshold;
            return (
              <div key={t.id} className={`border p-4 flex items-start gap-3 ${achieved ? 'border-white/10 bg-[#0d0d0f]' : 'border-white/5 bg-[#0d0d0f]/40 opacity-60'}`}>
                <div className="w-7 h-7 flex items-center justify-center shrink-0 border" style={{ borderColor: t.color }}>
                  {achieved ? <Check className="w-4 h-4" style={{ color: t.color }} /> : <span className="text-[10px] font-black" style={{ color: t.color }}>{t.threshold}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-black text-sm" style={{ color: t.color }}>{t.name}</p>
                    {t.threshold > 0 && <p className="text-white/30 text-[10px]">· {t.threshold.toLocaleString()} lifetime points</p>}
                    {achieved && <span className="text-[9px] font-bold tracking-wider text-green-400 bg-green-400/10 px-1.5 py-0.5">ACHIEVED</span>}
                  </div>
                  <p className="text-white/60 text-xs mt-1 leading-relaxed">{t.benefit}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}